'use client';

import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import Button from '@/components/elements/button';
import EventPaymentForm from '@/components/event-payment/event-payment-form';
import { useToggle } from '@/hooks/use-toggle';
import { useTranslation } from '@/i18n/client';
import { resetSelectedTickets } from '@/store/event-payment/event-payment.actions';
import { EventDetails } from '@/types/events';
import { WithLang } from '@/types/general';
import { CurrencyCode } from '@/types/general/base';
import { TicketsBookInput, TicketSelectOption } from '@/types/tickets';

interface Props {
    event: EventDetails;
    ticketOptions: TicketSelectOption[];
}

export default function EventPayment(props: WithLang<Props>) {
    const { t } = useTranslation(props.lang, 'common');
    const { toggleValue, handleToggle } = useToggle();
    const ticketsRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const pathName = usePathname();

    const [currencyCode, setCurrencyCode] = useState<CurrencyCode>('RSD');
    const methods = useForm<TicketsBookInput>();

    const isSoldOut = props.event.tickets.length > 0 && !props.event.tickets.some((item) => item.status === 'active');
    const withTickets =
        props.ticketOptions?.length > 0 && +props.event.visit_price > 0 && props.event.status === 'scheduled';

    const openPaymentForm = (item: { id: string; currency_code: CurrencyCode }) => {
        handleToggle();
        setTimeout(() => {
            if (ticketsRef.current) {
                ticketsRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 0);
        setCurrencyCode(item.currency_code);
    };

    const closePaymentForm = () => {
        handleToggle();
        window.scroll({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        dispatch(resetSelectedTickets());
    }, [dispatch, pathName]);

    return (
        <>
            <h3 className="md:w1/2 mb-4 text-left text-lg font-bold sm:w-1/2 md:ml-auto xl:w-[22%] xl:whitespace-pre">
                {t('common.buyTicketsOrRigister')}
            </h3>
            <div className="mt-auto flex w-full flex-col justify-between gap-y-4 sm:flex-row md:flex-col">
                {props.event.book_link && (
                    <Link
                        className="btn-accent flex-[.48] text-center text-black sm:mt-auto md:ml-auto md:w-1/2 md:flex-[1] xl:w-[22.5%]"
                        href={props.event.book_link}
                        target="_blank"
                    >
                        {t('register')}
                    </Link>
                )}
                {withTickets &&
                    props.event.payment_currency.map((item, index) => (
                        <>
                            <Button
                                key={item.currency_code}
                                disabled={toggleValue}
                                className="btn-accent flex-[.48] text-center text-black sm:mt-auto md:ml-auto md:w-1/2 md:flex-[1] xl:w-[22.5%]"
                                title={t('common.buy_currency', {
                                    returnObjects: true,
                                    currency_code: item.currency_code,
                                })}
                                onClick={() => openPaymentForm(item)}
                            />
                        </>
                    ))}
                {isSoldOut && (
                    <button
                        className="btn-accent flex-[.48] text-center text-black sm:mt-auto md:ml-auto md:w-1/2 md:flex-[1] xl:w-[22.5%]"
                        disabled={isSoldOut}
                    >
                        {t('common.soldOut')}
                    </button>
                )}
            </div>

            <FormProvider {...methods}>
                <div className="h-min" ref={ticketsRef}>
                    <EventPaymentForm
                        onClose={closePaymentForm}
                        currencyCode={currencyCode}
                        event={props.event}
                        ticketOptions={props.ticketOptions}
                        lang={props.lang}
                        isVisible={toggleValue}
                    />
                </div>
            </FormProvider>
        </>
    );
}
